import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { O2OPartnerBranchService } from "../../services/o2o-partner-branch.service";
import { AlertService } from "../../../share/service/alert.service";
import rolesData from "../../../../assets/json/roles.json";
import { O2OPartnerUserService } from "../../services/o2o-partner-user.service";
import { CommonSelectionInterface } from "../../../share/interface/common.interface";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  O2OPartnerUserAddInterface,
  O2OPartnerUserEditInterface,
  O2OPartnerUserListingInterface
} from "../../interfaces/o2o-partner.interface";
import userStatusesData from "../../../../assets/json/user-statuses.json";

export interface UserDialogData {
  id: string; // partner id
  user?: O2OPartnerUserListingInterface;
}

@Component({
  selector: "app-add-user-dialog",
  templateUrl: "./add-user-dialog.component.html",
  styleUrls: ["./add-user-dialog.component.scss"]
})
export class AddUserDialogComponent implements OnInit, OnDestroy {
  isEditMode = false;
  form = this.fb.group({
    name: ["", Validators.required],
    mobileNo: ["", Validators.required],
    email: ["", [Validators.required, Validators.email]],
    branchId: ["", Validators.required],
    role: ["", Validators.required],
    status: [""]
  });
  branches: CommonSelectionInterface[] = [];
  roles = rolesData;
  userStatuses = userStatusesData;

  private destroy$ = new Subject<void>();

  constructor(
    private alertService: AlertService,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddUserDialogComponent>,
    private o2oPartnerBranchService: O2OPartnerBranchService,
    private o2oPartnerUserService: O2OPartnerUserService,
    @Inject(MAT_DIALOG_DATA) public data: UserDialogData
  ) {
    this.isEditMode = !!this.data.user;
  }

  get isFormChanged(): boolean {
    if (!this.isEditMode) {
      return true;
    }

    const { user } = this.data;
    const { status, branchId, role } = this.form.value;

    return (
      status !== user.status || branchId !== user.branchId || role !== user.role
    );
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.form.patchValue({
        name: this.data.user.name,
        mobileNo: this.data.user.mobileNo,
        email: this.data.user.email,
        branchId: this.data.user.branchId,
        role: this.data.user.role,
        status: this.data.user.status
      });
      this.form.get("name").disable();
      this.form.get("mobileNo").disable();
      this.form.get("email").disable();

      this.filterUserStatuses();
    } else {
      this.userStatuses = this.userStatuses.filter(s => s.value === 'Pending');
      this.form.get('status').setValue('Pending');
    }

    this.getPartnerBranches();
    this.handleRoleChanges();
  }

  filterUserStatuses(): void {
    const currentStatus = this.data.user.status;
    if (currentStatus === "Pending") {
      this.userStatuses = this.userStatuses.filter((s) =>
        ["Pending"].includes(s.value)
      );
    } else if (currentStatus === "Active" || currentStatus === "Inactive") {
      this.userStatuses = this.userStatuses.filter((s) =>
        ["Active", "Inactive"].includes(s.value)
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleRoleChanges(): void {
    const roleControl = this.form.get("role");
    const branchControl = this.form.get("branchId");

    const updateBranchControl = (role: string, isInitial = false) => {
      if (role === "Admin") {
        if (!isInitial) {
          branchControl.setValue(null);
        }
        branchControl.disable();
        branchControl.clearValidators();
      } else {
        branchControl.enable();
        branchControl.setValidators([Validators.required]);
      }
      branchControl.updateValueAndValidity();
    };

    // Run on initialization
    updateBranchControl(roleControl.value, true);

    roleControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        updateBranchControl(role);
      });
  }

  getPartnerBranches(): void {
    this.o2oPartnerBranchService.getPartnerBranches(this.data.id).subscribe({
      next: (res) => {
        this.branches = res;
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onNext(): void {
    if (!this.form.valid || !this.isFormChanged) {
      return;
    }

    const payload = this.isEditMode
      ? this.getEditPayload()
      : this.getAddPayload();

    const request$ = this.isEditMode
      ? this.o2oPartnerUserService.update(this.data.id, payload as O2OPartnerUserEditInterface)
      : this.o2oPartnerUserService.add(this.data.id, payload as O2OPartnerUserAddInterface);

    request$.subscribe({
      next: () => {
        const action = this.isEditMode ? "updated" : "added";
        this.alertService.openSnackBar(`User ${action} successfully`);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.alertService.openSnackBar(err.error);
      }
    });
  }

  private getEditPayload(): O2OPartnerUserEditInterface {
    const { status, role, branchId } = this.form.value;
    const user = this.data.user;

    const payload: O2OPartnerUserEditInterface = {
      id: user.id
    };

    if (role !== user.role) payload.role = role;
    if (branchId !== user.branchId) payload.branchId = branchId;
    if (status !== user.status) payload.status = status;

    return payload;
  }

  private getAddPayload(): O2OPartnerUserAddInterface {
    const { name, email, mobileNo, role, branchId } = this.form.value;

    return {
      email,
      branchId,
      mobileNo,
      role,
      name
    };
  }
}
